import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  TDocType,
  TUploaderFileInfo,
} from '../components/common/uploader/types';

export interface IFileUploadState {
  allowedTypes: TDocType[];
  currentDocType: TDocType;
  createMode: boolean;
  files: Record<string, TUploaderFileInfo>;
  filesArray: TUploaderFileInfo[];
  inProgressFiles: File[];
  isDocTypeDisabled: boolean;
  selectedFiles: string[];
  translatedFiles: Record<string, Record<number | string, TUploaderFileInfo>>;
  useDocTypePermitted: boolean;
}
export interface IFileUploadSlice {
  uploaders: Record<string, IFileUploadState>;
}

function makeInitialState(): IFileUploadState {
  return {
    allowedTypes: [],
    createMode: false,
    currentDocType: {} as TDocType,
    isDocTypeDisabled: false,
    files: {},
    filesArray: [],
    inProgressFiles: [],
    selectedFiles: [],
    translatedFiles: {},
    useDocTypePermitted: false,
  };
}

const fileUploadSlice = createSlice({
  name: 'fileUploadSlice',
  initialState: {
    uploaders: {},
  } as IFileUploadSlice,
  reducers: {
    create(state, { payload: { id } }: PayloadAction<{ id: string }>) {
      state.uploaders[id] = makeInitialState();
    },
    setTranslationFile(
      state,
      {
        payload: { docId, id, file, langId },
      }: PayloadAction<{
        docId: string;
        id: string;
        file: TUploaderFileInfo;
        langId: number;
      }>,
    ) {
      if (!state.uploaders[id].translatedFiles[docId])
        state.uploaders[id].translatedFiles[docId] = {};
      state.uploaders[id].translatedFiles[docId][langId] = file;
    },
    update(
      state,
      {
        payload: { id, ...update },
      }: PayloadAction<
        Partial<Omit<IFileUploadState, 'files' | 'filesArray'>> & { id: string }
      >,
    ) {
      state.uploaders[id] = { ...state.uploaders[id], ...update };
    },
    updateArray(
      state,
      { payload }: PayloadAction<{ id: string; files: TUploaderFileInfo[] }>,
    ) {
      payload.files.forEach(
        (file) => (state.uploaders[payload.id].files[file.docId] = file),
      );
      state.uploaders[payload.id].filesArray = payload.files;
    },
    deleteDocument(
      state,
      { payload }: PayloadAction<{ id: string; docId: string }>,
    ) {
      const uploader = state.uploaders[payload.id];

      const translatedFilesKeys = Object.keys(uploader.translatedFiles);
      for (let i = 0; i < translatedFilesKeys.length; i++) {
        const translatedLangsKeys = Object.keys(
          uploader.translatedFiles[translatedFilesKeys[i]],
        );
        for (let j = 0; j < translatedLangsKeys.length; j++) {
          if (
            uploader.translatedFiles[translatedFilesKeys[i]][
              Number(translatedLangsKeys[j])
            ].docId === payload.docId
          ) {
            delete uploader.translatedFiles[translatedFilesKeys[i]][
              Number(translatedLangsKeys[j])
            ];
            return;
          }
        }
      }

      delete state.uploaders[payload.id].files[payload.docId];
      state.uploaders[payload.id].filesArray = state.uploaders[
        payload.id
      ].filesArray.filter((file) => file.docId !== payload.docId);
    },
    updateFile(
      state,
      { payload }: PayloadAction<TUploaderFileInfo & { id: string }>,
    ) {
      const uploader = state.uploaders[payload.id];

      const translatedFilesKeys = Object.keys(uploader.translatedFiles);
      for (let i = 0; i < translatedFilesKeys.length; i++) {
        const translatedLangsKeys = Object.keys(
          uploader.translatedFiles[translatedFilesKeys[i]],
        );
        for (let j = 0; j < translatedLangsKeys.length; j++) {
          if (
            uploader.translatedFiles[translatedFilesKeys[i]][
              Number(translatedLangsKeys[j])
            ].docId === payload.docId
          ) {
            uploader.translatedFiles[translatedFilesKeys[i]][
              Number(translatedLangsKeys[j])
            ] = payload;
            return;
          }
        }
      }

      state.uploaders[payload.id].files[payload.docId] = payload;
      state.uploaders[payload.id].filesArray = state.uploaders[
        payload.id
      ].filesArray.map((file) => {
        if (file.docId === payload.docId) return payload;
        return file;
      });
    },
    selectFile(
      state,
      {
        payload: { fileId, id },
      }: PayloadAction<{ id: string; fileId: string }>,
    ) {
      /** Solamente se seleccionan archivos en entity y process uploader */
      if (id.match(/^(E|P)/)) return;
      state.uploaders[id].selectedFiles = [fileId];
    },
    selectFiles(
      state,
      {
        payload: { filesIds, id },
      }: PayloadAction<{ id: string; filesIds?: string[] }>,
    ) {
      /** Solamente se seleccionan archivos en entity y process uploader */
      if (id.match(/^(E|P)/)) return;
      if (!filesIds || filesIds.length === 0)
        state.uploaders[id].selectedFiles = state.uploaders[id].filesArray.map(
          (current) => current.docId,
        );
      else
        state.uploaders[id].selectedFiles = [
          ...state.uploaders[id].selectedFiles,
          ...filesIds,
        ];
    },
    toggleFileSelection(
      state,
      {
        payload: { fileId, id },
      }: PayloadAction<{ id: string; fileId: string }>,
    ) {
      /** Solamente se seleccionan archivos en entity y process uploader */
      if (id.match(/^(E|P)/)) return;
      if (state.uploaders[id].selectedFiles.includes(fileId))
        state.uploaders[id].selectedFiles = state.uploaders[
          id
        ].selectedFiles.filter((current) => current !== fileId);
      else state.uploaders[id].selectedFiles.push(fileId);
    },
    unselectFiles(
      state,
      {
        payload: { filesIds, id },
      }: PayloadAction<{ id: string; filesIds?: string[] }>,
    ) {
      /** Solamente se seleccionan archivos en entity y process uploader */
      if (id.match(/^(E|P)/)) return;
      if (!filesIds || filesIds.length === 0)
        state.uploaders[id].selectedFiles = [];
      else
        state.uploaders[id].selectedFiles = state.uploaders[
          id
        ].selectedFiles.filter((current) => !filesIds.includes(current));
    },
  },
});

export const uploaderActions = fileUploadSlice.actions;

export default fileUploadSlice.reducer;
